/**
 * Video clock sync — keeps a playing <video> locked to a shared server epoch.
 *
 * The reveal broadcast carries `revealAt` (server clock). Every screen computes
 * where the video SHOULD be as (serverNow − syncEpoch) and converges on it by
 * RATE-NUDGING: playbackRate is bent by up to ±6% until the drift is inside a
 * ~20 ms deadband, then snaps back to exactly 1× (or the content's own rate).
 *
 * Why not seek? currentTime seeks flush the decoder, land late, and visibly
 * jump — with tiled-360 the walls form ONE continuous image, so even 100 ms
 * of disagreement shows on camera pans. Nudging is invisible (tiles are
 * muted) and converges 0.5 s of drift in a few seconds. Hard seeks remain
 * only for gross drift (> 1 s: a screen that just joined or woke up).
 * Loop-aware: drift is computed over the shortest path around the loop.
 *
 * @param {HTMLVideoElement} el
 * @param {object} opts
 * @param {number} opts.syncEpoch   server-clock ms when playback started
 * @param {number} opts.clockOffset serverTime − clientTime (ms), from clock-ping
 * @param {number} opts.playbackRate content's intended rate (default 1)
 * @returns {() => void} stop function
 */
export function startVideoClockSync(el, {
  syncEpoch,
  clockOffset = 0,
  intervalMs = 300,
  playbackRate = 1,
  seekThreshold = 1.0,   // seconds of drift before we hard-seek
  deadband = 0.02,       // inside this, run at exactly the content rate
  maxNudge = 0.06,       // ±6% — invisible on muted video, fast convergence
}) {
  if (!el || !syncEpoch) return () => {};

  const tick = () => {
    if (!el.duration || Number.isNaN(el.duration) || el.seeking || el.readyState < 2) return;
    // TV power-saving can silently pause a long-running element; wake it —
    // the drift logic below then reels it back to the shared clock
    if (el.paused) el.play().catch(() => {});
    const serverNow = Date.now() + clockOffset;
    // Slowed/sped playback scales expected position the same way
    let expected = ((serverNow - syncEpoch) / 1000) * playbackRate;
    if (expected < 0) return;
    if (el.loop) {
      expected %= el.duration;
    } else if (expected >= el.duration) {
      return; // clip is over everywhere; nothing to align
    }

    let drift = expected - el.currentTime;
    if (el.loop) {
      // Shortest path around the loop seam
      if (drift > el.duration / 2) drift -= el.duration;
      else if (drift < -el.duration / 2) drift += el.duration;
    }

    if (Math.abs(drift) > seekThreshold) {
      el.currentTime = (expected + el.duration) % el.duration;
      el.playbackRate = playbackRate;
      return;
    }

    if (Math.abs(drift) <= deadband) {
      if (el.playbackRate !== playbackRate) el.playbackRate = playbackRate;
      return;
    }

    // Proportional nudge: 0.5 s drift → full ±6%, 0.1 s → ~1.2%
    const nudge = Math.max(-maxNudge, Math.min(maxNudge, drift * 0.12));
    el.playbackRate = playbackRate * (1 + nudge);
  };

  const id = setInterval(tick, intervalMs);
  tick();
  return () => clearInterval(id);
}

export default startVideoClockSync;
